import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import {
  Box, Button, ButtonGroup, Flex, HStack, Text, Table, Thead, Tbody, Tr, Th, Td, Select, Badge, IconButton, Spinner, Tooltip, Input, Checkbox,
  InputGroup, InputLeftElement, Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, PopoverHeader, Portal, Textarea, Alert, AlertIcon,
  useColorModeValue, useToast,
} from "@chakra-ui/react"
import { CheckIcon, CloseIcon, DownloadIcon, EditIcon, RepeatIcon, SearchIcon, TriangleDownIcon, TriangleUpIcon, ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons"
import axios from "axios"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

/**
 * Orders ledger (masters) — redesigned 2026-09-14.
 *
 * Scope comes from dropdowns built from what the ledger actually holds for the selected
 * supplier (/orders/facets): the import file, the order date, the delivery date, the store.
 * The page opens on the LATEST import so a file you just uploaded is the first thing you see
 * (Imports and the Import Log link here with ?file=<id>). Three views over the same lines —
 * by store (sticky store headers), flat list (sortable columns), by PO — inside one fixed-height
 * scroll container. Search + quick filters are instant (client side); approve / reject /
 * comment per line, bulk per selection, Excel export of exactly the current selection.
 * docs/michael/pnp-orders/TASK-orders.md
 */

const API = process.env.REACT_APP_API_URL
const money = (n) => (n == null ? "-" : `R ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const num = (n, dp = 0) => (n == null || n === "" ? "-" : Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }))
const short = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s)

const articleLabel = (l) => {
  if (!l.article_code) return { text: "New line", color: "purple", tip: "No PnP article number yet — not in the sales, SDC or ranging feeds" }
  if (l.article_active === false) return { text: "Inactive", color: "gray", tip: `PnP article ${l.article_code} is inactive on the Articles page` }
  if (l.article_brand && /DISC$/i.test(String(l.article_brand).trim())) return { text: "DISC", color: "red", tip: `PnP article ${l.article_code} — discontinued brand group` }
  return { text: "Active", color: "green", tip: `PnP article ${l.article_code} (${l.article_match === "key" ? "matched by barcode / product code" : "matched by description"})` }
}

// sortable value per column key
const sortVal = (l, key) => {
  switch (key) {
    case "article": return articleLabel(l).text
    case "ranged": return l.ranging_status == null ? "" : l.ranging_status
    case "store": return `${l.store_code}`
    case "product": return l.description || ""
    case "comment": return l.line_comment || ""
    case "ordrec": return l.sr_order_qty == null ? null : Number(l.sr_order_qty)
    default: {
      const v = l[key]
      if (v == null || v === "") return null
      return ["pack_size", "qty", "units", "cost_price", "line_value", "soh_qty", "dros_qty", "days_cover", "new_days_cover"].includes(key) ? Number(v) : v
    }
  }
}
const cmp = (a, b) => {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === "number" && typeof b === "number") return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
}

const StoreCommentEditor = ({ storeCode, value, onSave }) => {
  const [text, setText] = useState(value || "")
  useEffect(() => { setText(value || "") }, [value])
  return (
    <Popover placement="bottom-end" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton aria-label="Edit store comment" icon={<EditIcon />} size="xs" variant="ghost" onClick={(e) => e.stopPropagation()} />
          </PopoverTrigger>
          <Portal>
            <PopoverContent onClick={(e) => e.stopPropagation()} w="380px">
              <PopoverArrow />
              <PopoverHeader fontSize="sm" fontWeight="semibold">Store comment — {storeCode}</PopoverHeader>
              <PopoverBody>
                <Textarea size="sm" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Shown on every order for this store (this supplier only)" />
                <HStack mt={2} justify="flex-end">
                  <Button size="xs" variant="ghost" onClick={onClose}>Cancel</Button>
                  <Button size="xs" colorScheme="blue" onClick={async () => { await onSave(text); onClose() }}>Save</Button>
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  )
}

const LINE_COLUMNS = [
  { key: "store", label: "Store", onlyList: true },
  { key: "po_number", label: "PO" },
  { key: "order_date", label: "Ordered" },
  { key: "delivery_date", label: "Deliver by" },
  { key: "po_type", label: "Type" },
  { key: "product", label: "Product" },
  { key: "pack_size", label: "Pack", numeric: true },
  { key: "qty", label: "Cases", numeric: true },
  { key: "units", label: "Units", numeric: true },
  { key: "cost_price", label: "Cost/case", numeric: true },
  { key: "line_value", label: "Value", numeric: true },
  { key: "article", label: "Article" },
  { key: "ranged", label: "Ranged" },
  { key: "soh_qty", label: "SOH", numeric: true },
  { key: "dros_qty", label: "DROS", numeric: true },
  { key: "days_cover", label: "Days", numeric: true },
  { key: "new_days_cover", label: "New days", numeric: true, bold: true },
  { key: "ordrec", label: "Ord/Rec", numeric: true },
  { key: "comment", label: "Comment" },
  { key: "status", label: "Status" },
]

const OrdersDashboard = ({ initialFileId = null }) => {
  const { selectedSupplier } = useSupplierRetailer()
  const toast = useToast()

  // scope (server side)
  const [facets, setFacets] = useState(null)
  const [fileId, setFileId] = useState(initialFileId ? String(initialFileId) : null) // null = not decided yet, "" = all imports
  const [orderDate, setOrderDate] = useState("")
  const [deliveryDate, setDeliveryDate] = useState("")
  const [store, setStore] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  // find (client side)
  const [search, setSearch] = useState("")
  const [quick, setQuick] = useState("all") // all | new | nosdc | commented
  const [poFilter, setPoFilter] = useState("")
  const [view, setView] = useState("store") // store | list | po
  const [sort, setSort] = useState({ key: "store", dir: "asc" })
  const [groupSort, setGroupSort] = useState("store")
  // data
  const [lines, setLines] = useState([])
  const [summary, setSummary] = useState(null)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [collapsed, setCollapsed] = useState({})
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [editingComment, setEditingComment] = useState({})
  const scrollRef = useRef(null)
  // request counters: a slow answer for the PREVIOUS supplier must never overwrite the current one
  // (it used to leave the page on "All imports" when the supplier switched while facets were loading)
  const facetsReq = useRef(0)
  const linesReq = useRef(0)

  // a new view or scope starts at the top-left of the scroll box (the list view is wide — the PO view must not inherit its offset)
  useEffect(() => { if (scrollRef.current) { scrollRef.current.scrollTop = 0; scrollRef.current.scrollLeft = 0 } }, [view, fileId, orderDate, deliveryDate, store, statusFilter, poFilter, quick])

  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const groupBg = useColorModeValue("blue.50", "blue.900")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const approvedBg = useColorModeValue("green.50", "green.900")
  const rejectedBg = useColorModeValue("red.50", "red.900")
  const panelBg = useColorModeValue("white", "gray.800")

  // ── facets: what exists for this supplier ─────────────────────────────────
  const loadFacets = useCallback(async () => {
    if (!selectedSupplier) return
    const my = ++facetsReq.current
    try {
      const res = await axios.post(`${API}/orders/facets`, { supplier: selectedSupplier })
      if (my !== facetsReq.current) return // superseded by a newer supplier
      const f = res.data.data
      setFacets(f)
      // first visit: open on the latest import (or the file we were sent to)
      setFileId((cur) => {
        if (cur !== null) return cur
        return f.files && f.files.length ? String(f.files[0].id) : ""
      })
    } catch (err) {
      toast({ title: "Could not load the orders overview", description: err.response?.data?.message || err.message, status: "error", duration: 4000 })
    }
  }, [selectedSupplier, toast])

  useEffect(() => {
    setFacets(null)
    setFileId(initialFileId ? String(initialFileId) : null)
    setOrderDate(""); setDeliveryDate(""); setStore(""); setPoFilter(""); setSearch(""); setQuick("all")
    loadFacets()
  }, [selectedSupplier, initialFileId, loadFacets])

  // ── lines for the current scope ───────────────────────────────────────────
  const fetchLines = useCallback(async () => {
    if (!selectedSupplier || fileId === null) return
    const my = ++linesReq.current
    setLoading(true)
    try {
      const body = { supplier: selectedSupplier, status: statusFilter }
      if (fileId) body.file_id = fileId
      if (orderDate) body.order_dates = [orderDate]
      if (deliveryDate) body.delivery_date = deliveryDate
      if (store) body.store = store
      const res = await axios.post(`${API}/orders/lines`, body)
      if (my !== linesReq.current) return // a newer scope is already loading
      setLines(res.data.data || [])
      setSummary(res.data.summary || null)
      setTruncated(!!res.data.truncated)
      setSelectedIds(new Set())
    } catch (err) {
      if (my === linesReq.current) toast({ title: "Could not load orders", description: err.response?.data?.message || err.message, status: "error", duration: 4000 })
    } finally {
      if (my === linesReq.current) setLoading(false)
    }
  }, [selectedSupplier, fileId, orderDate, deliveryDate, store, statusFilter, toast])

  useEffect(() => { fetchLines() }, [fetchLines])

  const patchLine = (id, patch) => setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/orders/update-status`, { id, status })
      patchLine(id, { status })
    } catch (err) {
      toast({ title: "Update failed", description: err.response?.data?.message || err.message, status: "error", duration: 3000 })
    }
  }
  const saveLineComment = async (id, line_comment) => {
    try {
      await axios.post(`${API}/orders/update-comment`, { id, line_comment })
      patchLine(id, { line_comment })
    } catch (err) {
      toast({ title: "Comment not saved", description: err.response?.data?.message || err.message, status: "error", duration: 3000 })
    } finally {
      setEditingComment((prev) => { const n = { ...prev }; delete n[id]; return n })
    }
  }
  const bulkStatus = async (status) => {
    const ids = [...selectedIds]
    if (!ids.length) return
    try {
      await axios.post(`${API}/orders/bulk-update-status`, { ids, status })
      setLines((prev) => prev.map((l) => (selectedIds.has(l.id) ? { ...l, status } : l)))
      setSelectedIds(new Set())
      toast({ title: `${ids.length} line(s) ${status}`, status: "success", duration: 2000 })
    } catch (err) {
      toast({ title: "Bulk update failed", description: err.response?.data?.message || err.message, status: "error", duration: 3000 })
    }
  }
  const saveStoreComment = async (store_code, comment) => {
    try {
      await axios.post(`${API}/orders/upsert-store-comment`, { supplier: selectedSupplier, store_code, comment })
      setLines((prev) => prev.map((l) => (l.store_code === store_code ? { ...l, store_comment: comment } : l)))
      toast({ title: "Store comment saved", status: "success", duration: 1500 })
    } catch (err) {
      toast({ title: "Store comment not saved", description: err.response?.data?.message || err.message, status: "error", duration: 3000 })
    }
  }
  const exportXlsx = async () => {
    setDownloading(true)
    try {
      const body = { supplier: selectedSupplier, status: statusFilter, po: poFilter || null, search: search || null }
      if (fileId) body.file_id = fileId
      if (orderDate) body.order_dates = [orderDate]
      if (deliveryDate) body.delivery_date = deliveryDate
      if (store) body.store = store
      const res = await axios.post(`${API}/orders/export`, body, { responseType: "blob" })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement("a")
      a.href = url
      a.setAttribute("download", `orders_${selectedSupplier}${orderDate ? "_" + orderDate : ""}.xlsx`)
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast({ title: "Export failed", description: err.message, status: "error", duration: 3000 })
    } finally {
      setDownloading(false)
    }
  }

  // ── client-side find + sort ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    const po = poFilter.trim()
    return lines.filter((l) => {
      if (po && l.po_number !== po) return false
      if (quick === "new" && l.article_code) return false
      if (quick === "nosdc" && (l.soh_qty != null || !l.article_code)) return false
      if (quick === "commented" && !l.line_comment && !l.store_comment) return false
      if (!term) return true
      return [l.store_code, l.store_name, l.store_description, l.description, l.vendor_product_code, l.barcode, l.po_number, l.article_code]
        .some((v) => (v || "").toString().toLowerCase().includes(term))
    })
  }, [lines, search, poFilter, quick])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    const dir = sort.dir === "asc" ? 1 : -1
    arr.sort((a, b) => dir * cmp(sortVal(a, sort.key), sortVal(b, sort.key)) || cmp(a.store_code, b.store_code) || cmp(a.po_number, b.po_number))
    return arr
  }, [filtered, sort])

  const groups = useMemo(() => {
    const g = {}
    for (const l of sorted) {
      const key = l.store_code || "UNKNOWN"
      if (!g[key]) g[key] = { store_code: key, store_name: l.store_name || l.store_description, region: l.region, store_comment: l.store_comment, lines: [], cases: 0, value: 0, pos: new Set(), sdc: 0 }
      g[key].lines.push(l); g[key].cases += Number(l.qty) || 0; g[key].value += Number(l.line_value) || 0; g[key].pos.add(l.po_number); if (l.soh_qty != null) g[key].sdc++
    }
    const arr = Object.values(g)
    const by = { store: (a, b) => cmp(a.store_code, b.store_code), lines: (a, b) => b.lines.length - a.lines.length, cases: (a, b) => b.cases - a.cases, value: (a, b) => b.value - a.value, region: (a, b) => cmp(a.region, b.region) || cmp(a.store_code, b.store_code) }
    arr.sort(by[groupSort] || by.store)
    return arr
  }, [sorted, groupSort])

  const pos = useMemo(() => {
    const m = {}
    for (const l of filtered) {
      const k = l.po_number
      if (!m[k]) m[k] = { po_number: k, store_code: l.store_code, store_name: l.store_name || l.store_description, order_date: l.order_date, delivery_date: l.delivery_date, po_type: l.po_type, lines: 0, cases: 0, value: 0, pending: 0, approved: 0, rejected: 0, sdc: 0, sr_order: 0, sr_receipt: 0, hasSr: false }
      const p = m[k]
      p.lines++; p.cases += Number(l.qty) || 0; p.value += Number(l.line_value) || 0; p[l.status] = (p[l.status] || 0) + 1; if (l.soh_qty != null) p.sdc++
      if (l.sr_order_qty != null) { p.hasSr = true; p.sr_order += Number(l.sr_order_qty); p.sr_receipt += Number(l.sr_receipt_qty || 0) }
    }
    const arr = Object.values(m)
    const dir = sort.dir === "asc" ? 1 : -1
    const key = ["po_number", "store_code", "order_date", "delivery_date", "po_type", "lines", "cases", "value", "pending", "approved", "rejected", "sdc"].includes(sort.key) ? sort.key : "order_date"
    arr.sort((a, b) => dir * cmp(a[key], b[key]) || cmp(a.po_number, b.po_number))
    return arr
  }, [filtered, sort])

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "line_value" || key === "qty" || key === "units" ? "desc" : "asc" }))
  const sortIcon = (key) => (sort.key === key ? (sort.dir === "asc" ? <TriangleUpIcon ml={1} boxSize={2} /> : <TriangleDownIcon ml={1} boxSize={2} />) : null)
  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const toggleSelectMany = (list) => {
    const all = list.every((l) => selectedIds.has(l.id))
    setSelectedIds((prev) => { const n = new Set(prev); list.forEach((l) => (all ? n.delete(l.id) : n.add(l.id))); return n })
  }

  const currentFile = facets && fileId ? facets.files.find((f) => String(f.id) === String(fileId)) : null
  const statusBadge = (status) => <Badge colorScheme={{ approved: "green", rejected: "red", pending: "yellow" }[status] || "gray"}>{status}</Badge>

  if (!selectedSupplier) return <Text p={4} color="gray.500">Select a supplier to view orders</Text>

  // ── row renderer shared by the store view and the list view ───────────────
  const renderLine = (l, showStore) => {
    const art = articleLabel(l)
    const hasSdc = l.soh_qty != null
    return (
      <Tr key={l.id} _hover={{ bg: hoverBg }} bg={l.status === "approved" ? approvedBg : l.status === "rejected" ? rejectedBg : "transparent"}>
        <Td px={2}><Checkbox size="sm" isChecked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} /></Td>
        {showStore && (
          <Td fontSize="xs" whiteSpace="nowrap">
            <Tooltip label={l.store_comment ? `Store comment: ${l.store_comment}` : l.store_name || ""}>
              <Text><b>{l.store_code}</b> {short(l.store_name || l.store_description || "", 22)}{l.store_comment ? " 💬" : ""}</Text>
            </Tooltip>
          </Td>
        )}
        <Td fontSize="xs" whiteSpace="nowrap">
          <Text as="span" cursor="pointer" _hover={{ textDecoration: "underline" }} onClick={() => { setPoFilter(l.po_number); setView("list") }} title="Show this PO only">{l.po_number}</Text>
        </Td>
        <Td fontSize="xs" whiteSpace="nowrap">{l.order_date}</Td>
        <Td fontSize="xs" whiteSpace="nowrap">{l.delivery_date || "-"}</Td>
        <Td fontSize="xs" whiteSpace="nowrap">{(l.po_type || "").replace("Standard ", "")}</Td>
        <Td fontSize="xs" maxW="280px">
          <Tooltip label={`${l.vendor_product_code} · barcode ${l.barcode || "-"}${l.article_code ? ` · PnP ${l.article_code}` : ""}`}>
            <Text isTruncated>{l.description}</Text>
          </Tooltip>
          <Text color="gray.500" fontSize="0.7rem">{l.vendor_product_code}</Text>
        </Td>
        <Td isNumeric fontSize="xs">{num(l.pack_size)}</Td>
        <Td isNumeric fontSize="xs" fontWeight="semibold">{num(l.qty)}</Td>
        <Td isNumeric fontSize="xs">{num(l.units)}</Td>
        <Td isNumeric fontSize="xs" whiteSpace="nowrap">{money(l.cost_price)}</Td>
        <Td isNumeric fontSize="xs" whiteSpace="nowrap">{money(l.line_value)}</Td>
        <Td><Tooltip label={art.tip}><Badge colorScheme={art.color} fontSize="0.65em">{art.text}</Badge></Tooltip></Td>
        <Td fontSize="xs">
          {l.ranging_status == null ? (l.article_code ? <Text color="gray.400">no</Text> : "-") : l.ranging_status === "04" ? <Badge colorScheme="green" fontSize="0.65em">yes</Badge> : <Tooltip label={`Site article status ${l.ranging_status}`}><Badge fontSize="0.65em">{l.ranging_status}</Badge></Tooltip>}
        </Td>
        <Td isNumeric fontSize="xs" whiteSpace="nowrap">{hasSdc ? num(l.soh_qty) : <Tooltip label={l.article_code ? "Store + article not in PnP's SDC feed — the store does not carry it yet" : "No PnP article number yet"}><Text color="gray.400">{l.article_code ? "not in SDC" : "-"}</Text></Tooltip>}</Td>
        <Td isNumeric fontSize="xs">{hasSdc ? num(l.dros_qty, 3) : "-"}</Td>
        <Td isNumeric fontSize="xs">{hasSdc ? num(l.days_cover, 1) : "-"}</Td>
        <Td isNumeric fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
          {l.new_days_cover != null ? num(l.new_days_cover, 1) : hasSdc ? <Tooltip label="No rate of sale (DROS 0) — days cover cannot be worked out"><Text color="gray.400">no sales</Text></Tooltip> : "-"}
        </Td>
        <Td isNumeric fontSize="xs">{l.sr_order_qty != null ? <Tooltip label={`PnP strike rate for this PO: ordered ${l.sr_order_qty}, received ${l.sr_receipt_qty} (${l.sr_uom || "CS"})`}><Text>{num(l.sr_order_qty)}/{num(l.sr_receipt_qty)}</Text></Tooltip> : <Text color="gray.400">-</Text>}</Td>
        <Td>
          {editingComment[l.id] !== undefined ? (
            <Input size="xs" w="160px" autoFocus value={editingComment[l.id]}
              onChange={(e) => setEditingComment((prev) => ({ ...prev, [l.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") saveLineComment(l.id, editingComment[l.id]); if (e.key === "Escape") setEditingComment((prev) => { const n = { ...prev }; delete n[l.id]; return n }) }}
              onBlur={() => saveLineComment(l.id, editingComment[l.id])} />
          ) : (
            <Text fontSize="xs" cursor="pointer" color={l.line_comment ? "inherit" : "gray.400"} maxW="160px" isTruncated title={l.line_comment || "Click to add a comment"}
              onClick={() => setEditingComment({ ...editingComment, [l.id]: l.line_comment || "" })}>
              {l.line_comment || "…"}
            </Text>
          )}
        </Td>
        <Td>{statusBadge(l.status)}</Td>
        <Td px={1}>
          <HStack spacing={1}>
            <Tooltip label="Approve (deliver)"><IconButton aria-label="Approve" icon={<CheckIcon />} size="xs" colorScheme="green" variant={l.status === "approved" ? "solid" : "outline"} onClick={() => setStatus(l.id, "approved")} /></Tooltip>
            <Tooltip label="Reject (do not deliver)"><IconButton aria-label="Reject" icon={<CloseIcon />} size="xs" colorScheme="red" variant={l.status === "rejected" ? "solid" : "outline"} onClick={() => setStatus(l.id, "rejected")} /></Tooltip>
          </HStack>
        </Td>
      </Tr>
    )
  }

  const lineHeader = (showStore, sticky) => (
    <Thead position={sticky ? "sticky" : undefined} top={0} zIndex={sticky ? 2 : undefined} bg={headerBg}>
      <Tr>
        <Th px={2} w="28px">
          <Checkbox size="sm" isChecked={filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id))} isIndeterminate={filtered.some((l) => selectedIds.has(l.id)) && !filtered.every((l) => selectedIds.has(l.id))} onChange={() => toggleSelectMany(filtered)} />
        </Th>
        {LINE_COLUMNS.filter((c) => showStore || !c.onlyList).map((c) => (
          <Th key={c.key} isNumeric={c.numeric} cursor="pointer" userSelect="none" whiteSpace="nowrap" onClick={() => toggleSort(c.key)} fontWeight={c.bold ? "bold" : undefined}>
            {c.label}{sortIcon(c.key)}
          </Th>
        ))}
        <Th />
      </Tr>
    </Thead>
  )

  const totalsLine = facets && facets.totals

  return (
    <Box>
      {/* ── scope: what exists for this supplier ── */}
      <Flex gap={2} mb={2} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Import file</Text>
          <Select size="sm" w="360px" value={fileId ?? ""} onChange={(e) => setFileId(e.target.value)}>
            <option value="">All imports{totalsLine ? ` (${num(totalsLine.lines)} lines, ${totalsLine.d1 || "-"} → ${totalsLine.d2 || "-"})` : ""}</option>
            {(facets?.files || []).map((f) => (
              <option key={f.id} value={f.id}>{f.created_at} · {f.source} · {short(f.filename, 44)} · {f.n} lines{f.d1 ? ` · ${f.d1}${f.d2 !== f.d1 ? ` → ${f.d2}` : ""}` : ""}</option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Order date</Text>
          <Select size="sm" w="190px" value={orderDate} onChange={(e) => setOrderDate(e.target.value)}>
            <option value="">All dates</option>
            {(facets?.orderDates || []).map((d) => <option key={d.d} value={d.d}>{d.d} · {d.n} lines · {d.pos} PO{d.pos === 1 ? "" : "s"}</option>)}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Delivery date</Text>
          <Select size="sm" w="170px" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}>
            <option value="">Any</option>
            {(facets?.deliveryDates || []).map((d) => <option key={d.d} value={d.d}>{d.d} · {d.n}</option>)}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Store</Text>
          <Select size="sm" w="220px" value={store} onChange={(e) => setStore(e.target.value)}>
            <option value="">All stores{facets ? ` (${facets.stores.length})` : ""}</option>
            {(facets?.stores || []).map((s) => <option key={s.store_code} value={s.store_code}>{s.store_code} {short(s.name || "", 22)} · {s.n}</option>)}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Status</Text>
          <Select size="sm" w="130px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>
        </Box>
        <Tooltip label="Reload"><IconButton aria-label="Reload" icon={<RepeatIcon />} size="sm" variant="ghost" onClick={() => { loadFacets(); fetchLines() }} /></Tooltip>
      </Flex>

      {/* ── find + view ── */}
      <Flex gap={2} mb={2} align="center" wrap="wrap">
        <InputGroup size="sm" w="300px">
          <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
          <Input placeholder="Store, product, PO, barcode, product code…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>
        <ButtonGroup size="xs" isAttached variant="outline">
          {[["all", "All"], ["new", "New lines"], ["nosdc", "Not in SDC"], ["commented", "With comment"]].map(([k, label]) => (
            <Button key={k} onClick={() => setQuick(k)} colorScheme={quick === k ? "blue" : undefined} variant={quick === k ? "solid" : "outline"}>{label}</Button>
          ))}
        </ButtonGroup>
        {poFilter && <Badge colorScheme="blue" px={2} py={1} cursor="pointer" onClick={() => setPoFilter("")}>PO {poFilter} ✕</Badge>}
        <Flex ml="auto" gap={2} align="center" wrap="wrap">
          {view === "store" && (
            <Select size="xs" w="150px" value={groupSort} onChange={(e) => setGroupSort(e.target.value)}>
              <option value="store">Stores A–Z</option>
              <option value="lines">Most lines first</option>
              <option value="cases">Most cases first</option>
              <option value="value">Highest value first</option>
              <option value="region">By region</option>
            </Select>
          )}
          {view === "store" && (
            <ButtonGroup size="xs" variant="ghost">
              <Button onClick={() => setCollapsed({})}>Expand all</Button>
              <Button onClick={() => setCollapsed(Object.fromEntries(groups.map((g) => [g.store_code, true])))}>Collapse all</Button>
            </ButtonGroup>
          )}
          <ButtonGroup size="xs" isAttached variant="outline">
            {[["store", "By store"], ["list", "List"], ["po", "By PO"]].map(([k, label]) => (
              <Button key={k} onClick={() => setView(k)} colorScheme={view === k ? "blue" : undefined} variant={view === k ? "solid" : "outline"}>{label}</Button>
            ))}
          </ButtonGroup>
          {selectedIds.size > 0 && (
            <ButtonGroup size="sm">
              <Button colorScheme="green" onClick={() => bulkStatus("approved")}>Approve {selectedIds.size}</Button>
              <Button colorScheme="red" onClick={() => bulkStatus("rejected")}>Reject {selectedIds.size}</Button>
              <Button variant="outline" onClick={() => bulkStatus("pending")}>Reset</Button>
            </ButtonGroup>
          )}
          <Button size="sm" colorScheme="blue" leftIcon={<DownloadIcon />} onClick={exportXlsx} isDisabled={!filtered.length || downloading} isLoading={downloading}>Export Excel</Button>
        </Flex>
      </Flex>

      {/* ── what you are looking at ── */}
      {summary && (
        <Flex gap={3} mb={2} align="center" wrap="wrap" fontSize="xs">
          <Text color="gray.600" whiteSpace="nowrap">
            {currentFile ? <>Import <b>{short(currentFile.filename, 46)}</b> · {currentFile.created_at}</> : <b>All imports</b>}
            {orderDate ? <> · ordered <b>{orderDate}</b></> : ""}{deliveryDate ? <> · delivery <b>{deliveryDate}</b></> : ""}{store ? <> · store <b>{store}</b></> : ""}
          </Text>
          <Text color="gray.700" whiteSpace="nowrap">
            <b>{num(summary.lines)}</b> lines · <b>{num(summary.pos)}</b> POs · <b>{num(summary.stores)}</b> stores · <b>{num(summary.cases)}</b> cases · <b>{money(summary.value)}</b>
            {filtered.length !== lines.length ? <> · showing <b>{num(filtered.length)}</b></> : ""}
          </Text>
          <HStack spacing={1}>
            <Tooltip label="Lines whose store + article has a row in PnP's Site Days Cover feed. The rest are stores that do not carry the article yet."><Badge colorScheme="teal">SDC {num(summary.sdc)}/{num(summary.lines)}</Badge></Tooltip>
            <Tooltip label="Lines with no PnP article number yet"><Badge colorScheme="purple">New {num(summary.newLines)}</Badge></Tooltip>
            <Badge colorScheme="yellow">Pending {num(summary.pending)}</Badge>
            <Badge colorScheme="green">Approved {num(summary.approved)}</Badge>
            <Badge colorScheme="red">Rejected {num(summary.rejected)}</Badge>
            {truncated && <Badge colorScheme="orange">first 5,000 lines only — narrow the selection</Badge>}
          </HStack>
        </Flex>
      )}

      {/* ── the table, fixed height, scrolls inside ── */}
      <Box ref={scrollRef} borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} h="calc(100vh - 392px)" minH="360px" overflow="auto">
        {loading ? (
          <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
        ) : !lines.length ? (
          <Box p={6}>
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <Box>
                {totalsLine && totalsLine.lines > 0 ? (
                  <>
                    Nothing for this selection. {selectedSupplier} has <b>{num(totalsLine.lines)}</b> order lines across {totalsLine.d1} → {totalsLine.d2} in {facets.files.length} import(s).{" "}
                    <Button size="xs" ml={2} onClick={() => { setFileId(""); setOrderDate(""); setDeliveryDate(""); setStore(""); setStatusFilter("all") }}>Show everything</Button>
                  </>
                ) : (
                  <>No orders yet for {selectedSupplier}. Files arrive through the hourly PnP EDI pickup, or upload one under Imports → PNP → Orders (PnP PO file).</>
                )}
              </Box>
            </Alert>
          </Box>
        ) : !filtered.length ? (
          <Box p={6}><Text fontSize="sm" color="gray.500">No lines match the search / quick filter. <Button size="xs" ml={2} onClick={() => { setSearch(""); setQuick("all"); setPoFilter("") }}>Clear</Button></Text></Box>
        ) : view === "list" ? (
          <Table size="sm" variant="simple">
            {lineHeader(true, true)}
            <Tbody>{sorted.map((l) => renderLine(l, true))}</Tbody>
          </Table>
        ) : view === "po" ? (
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} zIndex={2} bg={headerBg}>
              <Tr>
                {[["po_number", "PO"], ["store_code", "Store"], ["order_date", "Ordered"], ["delivery_date", "Deliver by"], ["po_type", "Type"], ["lines", "Lines", true], ["cases", "Cases", true], ["value", "Value", true], ["sdc", "In SDC", true], ["pending", "Pending", true], ["approved", "Approved", true], ["rejected", "Rejected", true], ["ordrec", "Ord/Rec (strike rate)", true]].map(([k, label, numeric]) => (
                  <Th key={k} isNumeric={numeric} cursor="pointer" userSelect="none" whiteSpace="nowrap" onClick={() => toggleSort(k)}>{label}{sortIcon(k)}</Th>
                ))}
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {pos.map((p) => (
                <Tr key={p.po_number} _hover={{ bg: hoverBg }}>
                  <Td fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">{p.po_number}</Td>
                  <Td fontSize="xs" whiteSpace="nowrap"><b>{p.store_code}</b> {short(p.store_name || "", 26)}</Td>
                  <Td fontSize="xs" whiteSpace="nowrap">{p.order_date}</Td>
                  <Td fontSize="xs" whiteSpace="nowrap">{p.delivery_date || "-"}</Td>
                  <Td fontSize="xs" whiteSpace="nowrap">{(p.po_type || "").replace("Standard ", "")}</Td>
                  <Td isNumeric fontSize="xs">{num(p.lines)}</Td>
                  <Td isNumeric fontSize="xs">{num(p.cases)}</Td>
                  <Td isNumeric fontSize="xs" whiteSpace="nowrap">{money(p.value)}</Td>
                  <Td isNumeric fontSize="xs">{num(p.sdc)}/{num(p.lines)}</Td>
                  <Td isNumeric fontSize="xs">{p.pending ? <Badge colorScheme="yellow">{p.pending}</Badge> : "-"}</Td>
                  <Td isNumeric fontSize="xs">{p.approved ? <Badge colorScheme="green">{p.approved}</Badge> : "-"}</Td>
                  <Td isNumeric fontSize="xs">{p.rejected ? <Badge colorScheme="red">{p.rejected}</Badge> : "-"}</Td>
                  <Td isNumeric fontSize="xs">{p.hasSr ? `${num(p.sr_order)}/${num(p.sr_receipt)}` : <Text color="gray.400">-</Text>}</Td>
                  <Td><Button size="xs" variant="outline" rightIcon={<ChevronRightIcon />} onClick={() => { setPoFilter(p.po_number); setView("list") }}>Lines</Button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          groups.map((g) => {
            const open = !collapsed[g.store_code]
            return (
              <Box key={g.store_code} borderBottomWidth="1px" borderColor={borderColor}>
                <Flex position="sticky" top={0} left={0} zIndex={1} bg={groupBg} px={3} py={1.5} cursor="pointer" onClick={() => setCollapsed((c) => ({ ...c, [g.store_code]: !c[g.store_code] }))} align="center" justify="space-between" gap={3} wrap="wrap" borderBottomWidth="1px" borderColor={borderColor}>
                  <Flex align="center" gap={2} minW={0}>
                    <Checkbox size="sm" isChecked={g.lines.every((l) => selectedIds.has(l.id))} isIndeterminate={g.lines.some((l) => selectedIds.has(l.id)) && !g.lines.every((l) => selectedIds.has(l.id))} onChange={() => toggleSelectMany(g.lines)} onClick={(e) => e.stopPropagation()} />
                    {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    <Text fontWeight="bold">{g.store_code}</Text>
                    <Text fontSize="sm" isTruncated maxW="260px">{g.store_name}</Text>
                    {g.region && <Badge variant="outline" fontSize="0.6em">{g.region}</Badge>}
                    <Badge fontSize="0.65em">{g.lines.length} lines</Badge>
                    <Badge variant="subtle" fontSize="0.65em">{g.pos.size} PO{g.pos.size === 1 ? "" : "s"}</Badge>
                    <Badge variant="subtle" fontSize="0.65em">{num(g.cases)} cases</Badge>
                    <Badge variant="subtle" fontSize="0.65em">{money(g.value)}</Badge>
                    <Badge variant="subtle" fontSize="0.65em" colorScheme="teal">SDC {g.sdc}/{g.lines.length}</Badge>
                  </Flex>
                  <Flex align="center" gap={1} minW={0}>
                    {g.store_comment ? (
                      <Tooltip label={g.store_comment}><Badge colorScheme="purple" maxW="360px" isTruncated fontSize="0.65em">{g.store_comment}</Badge></Tooltip>
                    ) : (
                      <Text fontSize="xs" color="gray.400">no store comment</Text>
                    )}
                    <StoreCommentEditor storeCode={g.store_code} value={g.store_comment} onSave={(text) => saveStoreComment(g.store_code, text)} />
                  </Flex>
                </Flex>
                {open && (
                  <Table size="sm" variant="simple">
                    {lineHeader(false, false)}
                    <Tbody>{g.lines.map((l) => renderLine(l, false))}</Tbody>
                  </Table>
                )}
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}

export default OrdersDashboard
